import { toCalendarDateTime } from "@internationalized/date";
import {
  GlobalFormSchema,
  globalEventFormSchema,
} from "features/CenterContent/RoleContent/GlobalEvents/types/formSchema";
import {
  GlobalEventRequest,
  getGlobalRequestVisibility,
} from "features/CenterContent/RoleContent/GlobalEvents/types/requestSchema";
import { GlobalEventResponse } from "features/CenterContent/RoleContent/GlobalEvents/types/responseSchema";
import { getCurrentCalendarDateTime } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/transformers";
import { InfrastructureEventCreatedResponse } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/utils";
import { visibilitySchemaResponse } from "shared/form/visibilitySchema";
import { z } from "zod";

const contextsForGlobalWarning = [
  "DROPS",
  // "PASSENGER_INFORMATION_SYSTEM",
  "TRAIN_DRIVER",
  "CUSTOMER_ADVISOR",
  "CONDUCTOR",
  "VY_FRONT_PAGE",
  "DROPS_LOG",
];

const getDefaultValuesForEdit = (
  globalEvent: GlobalEventResponse,
): GlobalFormSchema => {
  const { visibility } = globalEvent;
  const prevFromTime =
    visibility.type !== "CONSTANT"
      ? toCalendarDateTime(visibility.fromTime)
      : getCurrentCalendarDateTime();
  const now = getCurrentCalendarDateTime();
  return {
    globalForm: {
      customTexts: globalEvent.customTexts[0] ?? {
        contexts: contextsForGlobalWarning,
        texts: { NOB: { title: "", description: "" } },
      },
      visibility: {
        type: "OPEN_ENDED",
        fromTime: prevFromTime > now ? prevFromTime : now,
      },
      incidentIds: globalEvent.referencedIncidentIds,
    },
  };
};
const getDefaultValuesFromInfrastructureEvent = (
  relatedInfrastructureEvent: InfrastructureEventCreatedResponse,
): GlobalFormSchema => {
  const visibility = visibilitySchemaResponse.safeParse(
    relatedInfrastructureEvent.visibility,
  );
  return {
    globalForm: {
      customTexts: {
        contexts: contextsForGlobalWarning,
        texts: relatedInfrastructureEvent.texts,
      },
      visibility: {
        type: "OPEN_ENDED",
        fromTime:
          visibility.success && visibility.data.type === "OPEN_ENDED"
            ? toCalendarDateTime(visibility.data.fromTime)
            : getCurrentCalendarDateTime(),
      },
      incidentIds: [relatedInfrastructureEvent.incidentId],
    },
  };
};

const getDefaultValues = (
  globalEventToEdit?: GlobalEventResponse,
  relatedInfrastructureEvent?: InfrastructureEventCreatedResponse,
): GlobalFormSchema => {
  if (globalEventToEdit) {
    return getDefaultValuesForEdit(globalEventToEdit);
  }

  if (relatedInfrastructureEvent) {
    return getDefaultValuesFromInfrastructureEvent(relatedInfrastructureEvent);
  }

  return {
    globalForm: {
      customTexts: {
        contexts: contextsForGlobalWarning,
        texts: {
          NOB: {
            title: "",
            description: "",
          },
        },
      },
      visibility: {
        type: "OPEN_ENDED",
        fromTime: getCurrentCalendarDateTime(),
      },
      incidentIds: [],
    },
  };
};

export const responseToGlobalInformationForm = getDefaultValues;

export const formToGlobalInformationRequest = (
  formState: z.infer<typeof globalEventFormSchema>,
  referencedIncidentId?: string,
): GlobalEventRequest => ({
  globalInformationType: "GLOBAL_GENERAL_WARNING",
  visibility: getGlobalRequestVisibility(formState.visibility),
  incidentIds: referencedIncidentId ? [referencedIncidentId] : [],
  countryCode: "NO",
  customTexts: [
    {
      contexts: formState.customTexts.contexts,
      texts: formState.customTexts.texts,
    },
  ],
});
